import React, { useState, useEffect, useCallback } from 'react';
import { Users, Send, Bell, BellOff, Eye, UserCheck, Building2, ChevronDown, Plus, X, RefreshCw, Shield, Clock, AlertTriangle, LogOut, Menu, Settings, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api } from '../../utils/api';
import DriverComplianceDetail from '../shared/DriverComplianceDetail';

const POOL_CONFIG = {
  OWN:             { label: 'Own',             color: 'emerald', icon: UserCheck, badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  OWNER_OPERATOR:  { label: 'Owner/Operator',  color: 'blue',    icon: Users,     badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  BUSINESS_FLEET:  { label: 'Business Fleet',  color: 'amber',   icon: Building2, badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const GroupViewDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('drivers');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pools, setPools] = useState({ own: [], owner_operator: [], business_fleet: [] });
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);

  // Requests state
  const [outgoing, setOutgoing] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [reqLoading, setReqLoading] = useState(false);

  // Driver states cache
  const [driverStates, setDriverStates] = useState({});

  const loadDrivers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/group-view/drivers');
      setPools({ own: res.own || [], owner_operator: res.owner_operator || [], business_fleet: res.business_fleet || [] });
      setTotalActive(res.total_active || 0);
    } catch (err) {
      if (err.message?.includes('does not exist')) {
        toast.error('Group View tables not yet created. Please run migration 005.');
      } else {
        toast.error('Failed to load drivers: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const [out, inc] = await Promise.all([
        api('/group-view/requests/outgoing'),
        api('/group-view/requests/incoming'),
      ]);
      setOutgoing(out.requests || []);
      setIncoming(inc.requests || []);
    } catch {
      // Non-critical — may fail if tables don't exist
    } finally {
      setReqLoading(false);
    }
  }, []);

  const loadDriverState = useCallback(async (driverId) => {
    // Individual state load - not used anymore (batch load instead)
    try {
      const state = await api(`/drivers/${driverId}/state`);
      setDriverStates(prev => ({ ...prev, [driverId]: state }));
    } catch {
      setDriverStates(prev => ({ ...prev, [driverId]: { error: true } }));
    }
  }, []);

  const loadAllDriverStates = useCallback(async () => {
    try {
      const data = await api('/group-view/driver-states');
      setDriverStates(data.states || {});
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    loadDrivers();
    loadRequests();
  }, [loadDrivers, loadRequests]);

  // Batch-load states once drivers are loaded
  useEffect(() => {
    const allActive = [...pools.own, ...pools.owner_operator, ...pools.business_fleet]
      .filter(s => s.status === 'ACTIVE');
    if (allActive.length > 0) loadAllDriverStates();
  }, [pools, loadAllDriverStates]);

  const toggleNotification = async (driverId, enabled) => {
    try {
      await api(`/group-view/notifications/${driverId}`, {
        method: 'PUT',
        body: JSON.stringify({ notifications_enabled: enabled }),
      });
      // Update local state
      setPools(prev => {
        const updated = { ...prev };
        for (const key of ['own', 'owner_operator', 'business_fleet']) {
          updated[key] = prev[key].map(s =>
            s.driver_id === driverId ? { ...s, notifications_enabled: enabled } : s
          );
        }
        return updated;
      });
      toast.success(`Notifications ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to update: ' + err.message);
    }
  };

  const revokeShare = async (shareId) => {
    try {
      await api(`/group-view/shares/${shareId}/revoke`, { method: 'POST' });
      toast.success('Consent ended');
      loadDrivers();
    } catch (err) {
      toast.error('Failed: ' + err.message);
    }
  };

  const tabs = [
    { id: 'drivers', label: 'Drivers', icon: Users },
    { id: 'requests', label: 'Requests', icon: Send, count: incoming.filter(r => r.status === 'PENDING').length },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex" data-testid="group-view-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-800 border-r border-slate-700 transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b border-slate-700">
          <h1 className="text-lg font-bold text-white">LAWD</h1>
          <p className="text-xs text-emerald-400">Group View</p>
        </div>
        <nav className="p-2 space-y-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                activeTab === tab.id ? 'bg-emerald-500/15 text-emerald-400' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`gv-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-auto bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{tab.count}</span>
              )}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full p-4 border-t border-slate-700">
          <div className="text-xs text-slate-500 mb-2 truncate">{user.email}</div>
          <button onClick={onLogout} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="gv-logout">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-slate-400"><Menu className="w-5 h-5" /></button>
          <h2 className="text-white font-medium">{tabs.find(t => t.id === activeTab)?.label}</h2>
          <span className="text-xs text-slate-500">{totalActive} active driver{totalActive !== 1 ? 's' : ''}</span>
          <button onClick={() => { loadDrivers(); loadRequests(); loadAllDriverStates(); }} className="ml-auto text-slate-400 hover:text-white" data-testid="gv-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </header>

        <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
          {activeTab === 'drivers' && (
            <DriversView
              pools={pools}
              loading={loading}
              driverStates={driverStates}
              onToggleNotification={toggleNotification}
              onRevoke={revokeShare}
              onRefreshState={loadDriverState}
            />
          )}
          {activeTab === 'requests' && (
            <RequestsView
              outgoing={outgoing}
              incoming={incoming}
              loading={reqLoading}
              onRefresh={loadRequests}
              onLoadDrivers={loadDrivers}
            />
          )}
          {activeTab === 'settings' && (
            <div className="text-slate-400 text-sm">Group View settings coming soon.</div>
          )}
        </div>
      </main>
    </div>
  );
};


/* ====== Drivers View ====== */
const DriversView = ({ pools, loading, driverStates, onToggleNotification, onRevoke, onRefreshState }) => {
  const [poolFilter, setPoolFilter] = useState('ALL');

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;
  }

  const allEmpty = !pools.own.length && !pools.owner_operator.length && !pools.business_fleet.length;

  if (allEmpty) {
    return (
      <div className="text-center py-16 text-slate-500">
        <Eye className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>No drivers in your Group View yet</p>
        <p className="text-xs mt-1">Go to Requests to send share requests to drivers or businesses</p>
      </div>
    );
  }

  const poolDefs = [
    { key: 'ALL', label: 'All Drivers', count: (pools.own?.length || 0) + (pools.owner_operator?.length || 0) + (pools.business_fleet?.length || 0) },
    { key: 'BUSINESS_FLEET', label: 'Business Fleet', count: pools.business_fleet?.length || 0 },
    { key: 'OWNER_OPERATOR', label: 'Owner/Operator', count: pools.owner_operator?.length || 0 },
    { key: 'OWN', label: 'Own Drivers', count: pools.own?.length || 0 },
  ].filter(p => p.key === 'ALL' || p.count > 0);

  // If no business fleet, default to showing Owner/Operator
  const visiblePools = poolFilter === 'ALL'
    ? ['OWN', 'OWNER_OPERATOR', 'BUSINESS_FLEET']
    : [poolFilter];

  return (
    <div className="space-y-4">
      {/* Pool Filter Bar */}
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="pool-filter-bar">
        {poolDefs.map(p => (
          <button
            key={p.key}
            onClick={() => setPoolFilter(p.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${
              poolFilter === p.key
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-slate-800/50 text-slate-400 border border-slate-700 hover:text-white hover:bg-slate-700/50'
            }`}
            data-testid={`pool-filter-${p.key.toLowerCase()}`}
          >
            {p.label}
            <span className="px-1.5 py-0 rounded-full bg-slate-700/50 text-[10px]">{p.count}</span>
          </button>
        ))}
      </div>

      {/* Pool Sections */}
      {visiblePools.map(poolKey => {
        const drivers = pools[poolKey.toLowerCase()] || [];
        if (!drivers.length) return null;
        const cfg = POOL_CONFIG[poolKey];
        return (
          <PoolSection
            key={poolKey}
            poolKey={poolKey}
            config={cfg}
            drivers={drivers}
            driverStates={driverStates}
            onToggleNotification={onToggleNotification}
            onRevoke={onRevoke}
            onRefreshState={onRefreshState}
          />
        );
      })}
    </div>
  );
};


/* ====== Pool Section ====== */
const PoolSection = ({ poolKey, config, drivers, driverStates, onToggleNotification, onRevoke, onRefreshState }) => {
  const [expanded, setExpanded] = useState(true);
  const active = drivers.filter(d => d.status === 'ACTIVE');
  const ended = drivers.filter(d => d.status === 'ENDED');
  const Icon = config.icon;

  return (
    <div className="bg-slate-800/50 rounded-2xl border border-slate-700" data-testid={`pool-${poolKey.toLowerCase()}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 border-b border-slate-700/50"
      >
        <Icon className={`w-5 h-5 text-${config.color}-400`} />
        <span className="text-white font-medium text-sm">{config.label}</span>
        <span className="text-xs text-slate-500">{active.length} active{ended.length ? `, ${ended.length} ended` : ''}</span>
        <ChevronDown className={`w-4 h-4 text-slate-500 ml-auto transition ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="divide-y divide-slate-700/40">
          {active.map(share => (
            <DriverRow
              key={share.share_id}
              share={share}
              poolKey={poolKey}
              state={driverStates[share.driver_id]}
              onToggleNotification={onToggleNotification}
              onRevoke={onRevoke}
              onRefreshState={onRefreshState}
            />
          ))}
          {ended.map(share => (
            <div key={share.share_id} className="px-4 py-3 opacity-50" data-testid={`ended-${share.driver_id}`}>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-sm">{share.driver_name}</span>
                <span className="text-[10px] px-2 py-0.5 rounded border bg-slate-700/40 text-slate-500 border-slate-600/30">
                  Consent ended {share.revoked_at ? new Date(share.revoked_at).toLocaleDateString('en-AU') : ''}
                </span>
                {share.driver_client_name && (
                  <span className="text-[10px] text-slate-600">{share.driver_client_name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ====== Driver Row ====== */
const DriverRow = ({ share, poolKey, state, onToggleNotification, onRevoke, onRefreshState }) => {
  const [expanded, setExpanded] = useState(false);
  const [fullState, setFullState] = useState(null);
  const [fullLoading, setFullLoading] = useState(false);
  const cfg = POOL_CONFIG[poolKey];
  const isLoading = !state;
  const hasError = state?.error;
  const currentState = state?.current_state || 'UNKNOWN';
  const continuous = state?.continuous_work_seconds || 0;
  const work24h = state?.work_24h_seconds || 0;
  const isWorking = currentState === 'WORK';
  const isResting = currentState === 'REST';
  const regime = state?.regime || '';

  const formatDur = (s) => {
    if (!s || s <= 0) return '0h 0m';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  };

  const handleExpand = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    if (newExpanded && !fullState && share.driver_id) {
      setFullLoading(true);
      try {
        const data = await api(`/drivers/${share.driver_id}/state`);
        setFullState(data);
      } catch { /* ignore */ }
      finally { setFullLoading(false); }
    }
  };

  const stateColor = isWorking ? 'bg-red-500' : isResting ? 'bg-emerald-500' : 'bg-slate-500';
  const stateText = isWorking ? 'text-red-400' : isResting ? 'text-emerald-400' : 'text-slate-400';
  const stateLabel = isWorking ? 'WORK' : isResting ? 'REST' : currentState;

  return (
    <div className="hover:bg-slate-700/10 transition" data-testid={`driver-row-${share.driver_id}`}>
      {/* Main Row */}
      <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={handleExpand}>
        {/* Live state dot */}
        <div className={`w-2.5 h-2.5 rounded-full ${stateColor} flex-shrink-0 ${isWorking ? 'animate-pulse' : ''}`} />

        {/* Name + Pool */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium truncate">{share.driver_name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${cfg.badge} hidden sm:inline`}>
              {cfg.label}
            </span>
          </div>
          {share.driver_client_name && poolKey === 'BUSINESS_FLEET' && (
            <span className="text-[10px] text-slate-500">{share.driver_client_name}</span>
          )}
        </div>

        {/* Status + Stats */}
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" />
        ) : hasError ? (
          <span className="text-[10px] text-red-400">Unavailable</span>
        ) : (
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* State badge */}
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
              isWorking ? 'bg-red-500/20 text-red-400' : isResting ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-600/20 text-slate-400'
            }`}>
              {stateLabel}
            </span>

            {/* Continuous work if working */}
            {isWorking && continuous > 0 && (
              <span className={`text-xs font-mono hidden sm:inline ${continuous > 18000 ? 'text-amber-400' : 'text-slate-400'}`}>
                {formatDur(continuous)}
              </span>
            )}

            {/* 24h work */}
            <span className="text-[10px] text-slate-500 hidden md:inline">24h: {formatDur(work24h)}</span>

            {/* Regime */}
            <span className="text-[10px] text-slate-500 hidden lg:inline">{regime}</span>

            {/* Compliance */}
            {state?.compliance_status && state.compliance_status !== 'UNKNOWN' && (
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                state.compliance_status === 'OK' ? 'bg-emerald-500' :
                state.compliance_status === 'WARNING' ? 'bg-amber-500' : 'bg-red-500'
              }`} title={state.compliance_status} />
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleNotification(share.driver_id, !share.notifications_enabled); }}
            className={`p-1.5 rounded-lg transition ${
              share.notifications_enabled ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-slate-600 hover:bg-slate-700'
            }`}
            title={share.notifications_enabled ? 'Notifications on' : 'Notifications off'}
            data-testid={`notif-toggle-${share.driver_id}`}
          >
            {share.notifications_enabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </button>
          {poolKey !== 'OWN' && (
            <button
              onClick={(e) => { e.stopPropagation(); onRevoke(share.share_id); }}
              className="p-1.5 rounded-lg text-slate-600 hover:bg-red-500/10 hover:text-red-400 transition"
              title="End consent"
              data-testid={`revoke-${share.share_id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <ChevronDown className={`w-3.5 h-3.5 text-slate-600 transition ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded Full Compliance Detail */}
      {expanded && state && !hasError && (
        <div className="px-4 pb-3">
          <div className={`rounded-xl border p-3 ${
            state.compliance_status === 'CRITICAL' ? 'border-red-500/30 bg-red-500/5' :
            state.compliance_status === 'WARNING' ? 'border-amber-500/30 bg-amber-500/5' :
            'border-slate-700/50 bg-slate-800/30'
          }`}>
            {fullLoading ? (
              <div className="flex items-center gap-2 py-3">
                <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-slate-400">Loading full compliance...</span>
              </div>
            ) : fullState ? (
              <DriverComplianceDetail state={fullState} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-slate-700/20 rounded-lg p-2.5">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Status</div>
                  <div className={`text-sm font-semibold ${stateText}`}>{stateLabel}</div>
                  {isWorking && continuous > 0 && (
                    <div className="text-[10px] text-slate-500">for {formatDur(continuous)}</div>
                  )}
                </div>
                <div className="bg-slate-700/20 rounded-lg p-2.5">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">24h Work</div>
                  <div className="text-sm font-semibold text-white">{formatDur(work24h)}</div>
                </div>
                <div className="bg-slate-700/20 rounded-lg p-2.5">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Regime</div>
                  <div className="text-sm font-semibold text-white">{state.regime_label || regime || '-'}</div>
                </div>
                <div className="bg-slate-700/20 rounded-lg p-2.5">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Compliance</div>
                  <div className={`text-sm font-semibold ${
                    state.compliance_status === 'OK' ? 'text-emerald-400' :
                    state.compliance_status === 'WARNING' ? 'text-amber-400' : 'text-red-400'
                  }`}>
                    {state.compliance_status === 'WARNING' && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                    {state.compliance_status || '-'}
                  </div>
                </div>
              </div>
            )}
            {fullState && state.last_work_end && isResting && (
              <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last work ended {new Date(state.last_work_end).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};


/* ====== Requests View ====== */
const RequestsView = ({ outgoing, incoming, loading, onRefresh, onLoadDrivers }) => {
  const [showNew, setShowNew] = useState(false);

  return (
    <div className="space-y-6">
      {/* New Request */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setShowNew(!showNew)}
          className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 text-sm flex items-center gap-1.5"
          data-testid="new-request-btn"
        >
          <Plus className="w-4 h-4" /> New Request
        </button>
      </div>

      {showNew && <NewRequestForm onClose={() => setShowNew(false)} onSuccess={() => { setShowNew(false); onRefresh(); }} />}

      {/* Incoming */}
      {incoming.length > 0 && (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700">
          <div className="px-4 py-3 border-b border-slate-700">
            <h3 className="text-white text-sm font-medium">Incoming Requests</h3>
          </div>
          <div className="divide-y divide-slate-700/40">
            {incoming.map(req => (
              <IncomingRequestRow key={req.request_id} request={req} onRefresh={() => { onRefresh(); onLoadDrivers(); }} />
            ))}
          </div>
        </div>
      )}

      {/* Outgoing */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700">
        <div className="px-4 py-3 border-b border-slate-700">
          <h3 className="text-white text-sm font-medium">Outgoing Requests <span className="text-slate-500 font-normal">({outgoing.length})</span></h3>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-emerald-400 animate-spin" /></div>
        ) : outgoing.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">No requests sent yet</div>
        ) : (
          <div className="divide-y divide-slate-700/40">
            {outgoing.map(req => (
              <OutgoingRequestRow key={req.request_id} request={req} onRefresh={onRefresh} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/* ====== New Request Form ====== */
const NewRequestForm = ({ onClose, onSuccess }) => {
  const [type, setType] = useState('OWNER_OPERATOR');
  const [driverEmail, setDriverEmail] = useState('');
  const [clientId, setClientId] = useState('');
  const [message, setMessage] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (type === 'BUSINESS_FLEET') {
      api('/group-view/businesses').then(r => setBusinesses(r.businesses || [])).catch(() => {});
    }
  }, [type]);

  const submit = async () => {
    if (type === 'OWNER_OPERATOR' && !driverEmail.trim()) {
      toast.error('Please enter the driver\'s email address');
      return;
    }
    setSubmitting(true);
    try {
      const body = { target_type: type, message: message || undefined };
      if (type === 'OWNER_OPERATOR') body.target_driver_email = driverEmail.trim();
      else body.target_client_id = clientId;

      await api('/group-view/requests', { method: 'POST', body: JSON.stringify(body) });
      toast.success('Request sent');
      onSuccess();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-800/70 rounded-2xl border border-slate-700 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-white text-sm font-medium">New Share Request</h3>
        <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Request Type</label>
          <select
            value={type}
            onChange={e => setType(e.target.value)}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
            data-testid="request-type-select"
          >
            <option value="OWNER_OPERATOR">Owner/Operator (individual driver)</option>
            <option value="BUSINESS_FLEET">Business Fleet (company)</option>
          </select>
        </div>

        {type === 'OWNER_OPERATOR' ? (
          <div>
            <label className="block text-xs text-slate-400 mb-1">Driver Email</label>
            <input
              type="email"
              value={driverEmail}
              onChange={e => setDriverEmail(e.target.value)}
              placeholder="driver@example.com"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
              data-testid="request-driver-email"
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs text-slate-400 mb-1">Business</label>
            <select
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
              data-testid="request-business-select"
            >
              <option value="">Select business</option>
              {businesses.map(b => (
                <option key={b.client_id} value={b.client_id}>{b.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label className="block text-xs text-slate-400 mb-1">Message (optional)</label>
        <input
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Why are you requesting access?"
          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
          data-testid="request-message"
        />
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white">Cancel</button>
        <button
          onClick={submit}
          disabled={submitting || (type === 'OWNER_OPERATOR' ? !driverEmail.trim() : !clientId)}
          className="px-4 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-500 disabled:opacity-40"
          data-testid="send-request-btn"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Request'}
        </button>
      </div>
    </div>
  );
};


/* ====== Request Rows ====== */
const OutgoingRequestRow = ({ request, onRefresh }) => {
  const cancel = async () => {
    try {
      await api(`/group-view/requests/${request.request_id}/cancel`, { method: 'POST' });
      toast.success('Request cancelled');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const statusStyle = {
    PENDING: 'text-amber-400 bg-amber-500/10',
    APPROVED: 'text-emerald-400 bg-emerald-500/10',
    DECLINED: 'text-red-400 bg-red-500/10',
    CANCELLED: 'text-slate-500 bg-slate-700/30',
  }[request.status] || 'text-slate-400';

  return (
    <div className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`outgoing-${request.request_id}`}>
      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
        request.target_type === 'OWNER_OPERATOR' ? POOL_CONFIG.OWNER_OPERATOR.badge : POOL_CONFIG.BUSINESS_FLEET.badge
      }`}>
        {request.target_type === 'OWNER_OPERATOR' ? 'Owner/Op' : 'Business'}
      </span>
      <span className="text-white">
        {request.target_driver_name || request.target_client_name || request.target_driver_id || request.target_client_id}
      </span>
      <span className={`text-[10px] px-2 py-0.5 rounded ${statusStyle}`}>{request.status}</span>
      <span className="text-xs text-slate-600 ml-auto">{new Date(request.requested_at).toLocaleDateString('en-AU')}</span>
      {request.status === 'PENDING' && (
        <button onClick={cancel} className="text-slate-500 hover:text-red-400 text-xs" data-testid={`cancel-${request.request_id}`}>Cancel</button>
      )}
    </div>
  );
};

const IncomingRequestRow = ({ request, onRefresh }) => {
  const [responding, setResponding] = useState(false);

  const respond = async (status) => {
    setResponding(true);
    try {
      await api(`/group-view/requests/${request.request_id}/respond`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      });
      toast.success(status === 'APPROVED' ? 'Access granted' : 'Request declined');
      onRefresh();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResponding(false);
    }
  };

  return (
    <div className="px-4 py-3 flex items-center gap-3 text-sm" data-testid={`incoming-${request.request_id}`}>
      <span className="text-white">{request.requester_name || 'Unknown'}</span>
      <span className="text-slate-500 text-xs">wants to view your drivers</span>
      {request.message && <span className="text-slate-400 text-xs italic">{request.message}</span>}
      <span className={`text-[10px] px-2 py-0.5 rounded ${
        request.status === 'PENDING' ? 'text-amber-400 bg-amber-500/10' : 'text-slate-500 bg-slate-700/30'
      }`}>{request.status}</span>
      {request.status === 'PENDING' && (
        <div className="flex gap-1.5 ml-auto">
          <button
            onClick={() => respond('APPROVED')}
            disabled={responding}
            className="px-2.5 py-1 bg-emerald-600 text-white text-xs rounded-lg hover:bg-emerald-500 flex items-center gap-1"
            data-testid={`approve-${request.request_id}`}
          >
            <CheckCircle2 className="w-3 h-3" /> Approve
          </button>
          <button
            onClick={() => respond('DECLINED')}
            disabled={responding}
            className="px-2.5 py-1 bg-slate-700 text-slate-300 text-xs rounded-lg hover:bg-red-500/20 hover:text-red-400 flex items-center gap-1"
            data-testid={`decline-${request.request_id}`}
          >
            <XCircle className="w-3 h-3" /> Decline
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupViewDashboard;
